import React from "react";
import { cloneDeep } from "lodash";
import { MdCheck } from "react-icons/md";
import IFamily from "../models/IFamily";
import { ELCID, IMediaToolkitNavPage } from "../models/IMediaToolkit";
import { useNavigate } from "react-router-dom";
import AAPService from "../services/AAPService";
import { ITranslations } from "../data/strings";

export interface IPrioritiesPageProps extends IMediaToolkitNavPage {
    togglePriority: (pageId: number) => void;
    updateFamily: (family: IFamily) => void;
    translations: ITranslations;
}

export const PrioritiesPage: React.FunctionComponent<IPrioritiesPageProps> = (props) => {
    // bail if no family data is filled out
    const navigate = useNavigate();

    React.useEffect(() => {
        if (props.family.name === "" || props.family.members[0].name === "") {
            navigate("/family");
        }
    });

    const allSelected = props.family.priorities.length >= props.pages.filter((page) => page.availablePriority).length;

    return (
        <>
            <div className="card-header bg-aap-creamy-gray-500 text-aap-dk-red-500 px-5 xl:px-10 py-5 pt-10">
                <div>
                    <h1 className="text-4xl mb-5">{props.title}</h1>
                    <div className="text-sm" dangerouslySetInnerHTML={{ __html: props.description }}></div>
                </div>
            </div>
            <div className="card-body bg-aap-cream-500 p-5 pt-0 xl:p-10 xl:pt-0">
                {props.ctaMap}
                <div>
                    {!props.readonly ? (
                        <button
                            type="button"
                            className="text-aap-red-500 text-xs"
                            id={`btn_priorities_toggleAll_${ELCID[props.lcid]}`}
                            data-action={allSelected ? `unselectAll` : `selectAll`}
                            onClick={() => {
                                const tempFamily = cloneDeep(props.family);
                                if (allSelected) {
                                    // remove all
                                    tempFamily.priorities = [];
                                } else {
                                    props.pages.forEach((page) => {
                                        if (
                                            page.availablePriority &&
                                            tempFamily.priorities.indexOf(page.spoId) === -1
                                        ) {
                                            tempFamily.priorities.push(page.spoId);
                                        }
                                    });
                                }

                                props.updateFamily(tempFamily);
                            }}
                        >
                            {allSelected ? props.translations.unselectAll : props.translations.selectAll}
                        </button>
                    ) : null}
                    <hr className="my-1 border-aap-gray-200" />
                </div>
                <div className="md:flex flex-wrap">
                    {props.pages
                        .filter((page) => page.availablePriority)
                        .map((page, pageIdx) => {
                            const selected = props.family.priorities.indexOf(page.spoId) > -1;

                            return (
                                <label
                                    className="flex gap-3 md:w-1/2 md:basis-1/2 cursor-pointer group box-border py-8 md:py-5 md:odd:pr-5 md:even:pl-5"
                                    key={`priority-item-${pageIdx}`}
                                    id={`btn_priorities_item_${ELCID[props.lcid]}_${pageIdx}`}
                                    data-priority-title={page.title}
                                    htmlFor={`cbox_priority_${page.title}`}
                                    aria-label={page.title}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected}
                                        id={`cbox_priority_${page.title}`}
                                        onChange={() => props.togglePriority(page.spoId)}
                                        className="hidden"
                                        aria-labelledby={`btn_priorities_item_${ELCID[props.lcid]}_${pageIdx}`}
                                    />
                                    <div
                                        className={`w-10 h-10 shrink-0 grow-0 relative border rounded-sm text-white group-hover:bg-aap-green-200 transition-all duration-300 ${
                                            selected
                                                ? `bg-aap-green-500 group-hover:bg-aap-green-700`
                                                : `border-aap-gray-300 bg-white`
                                        }`}
                                    >
                                        {selected ? (
                                            <MdCheck
                                                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                                                size={24}
                                            />
                                        ) : null}
                                        <div className="absolute -left-0 -top-0 -translate-x-1/2 -translate-y-1/2 text-aap-gray-400 z-20 w-8 h-8 bg-white border border-aap-gray-200 rounded-full">
                                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                                                {React.createElement(AAPService.GetMaterialIcon(page.icon), {
                                                    size: 24,
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <h4
                                            className={`text-lg mb-1 group-hover:underline ${
                                                selected ? `font-semibold` : ``
                                            }`}
                                        >
                                            {page.title}
                                        </h4>
                                        <p
                                            className="text-xs text-aap-gray-700"
                                            dangerouslySetInnerHTML={{ __html: page.description || "" }}
                                        ></p>
                                    </div>
                                </label>
                            );
                        })}
                </div>
                <div>
                    <hr className="my-1 border-aap-gray-200" />
                    {!props.readonly ? (
                        <button
                            type="button"
                            className="text-aap-red-500 text-xs"
                            id={`btn_priorities_toggleAll_${ELCID[props.lcid]}`}
                            data-action={allSelected ? `unselectAll` : `selectAll`}
                            onClick={() => {
                                const tempFamily = cloneDeep(props.family);
                                if (allSelected) {
                                    // remove all
                                    tempFamily.priorities = [];
                                } else {
                                    props.pages.forEach((page) => {
                                        if (
                                            page.availablePriority &&
                                            tempFamily.priorities.indexOf(page.spoId) === -1
                                        ) {
                                            tempFamily.priorities.push(page.spoId);
                                        }
                                    });
                                }

                                props.updateFamily(tempFamily);
                            }}
                        >
                            {allSelected ? props.translations.unselectAll : props.translations.selectAll}
                        </button>
                    ) : null}
                </div>
                {props.footer ? (
                    <div className="pt-10">
                        <div dangerouslySetInnerHTML={{ __html: props.footer || "" }}></div>
                    </div>
                ) : null}
                <div className="mobileNav py-2 flex justify-between lg:hidden">
                    {props.backButton}
                    {props.continueButton}
                </div>
            </div>
        </>
    );
};
